// Outils communs aux scénarios du labyrinthe — fonctions pures, sans DOM.
// Un scénario est un module { id, create, tick, enter, gain, hints, isIdle } :
//   create(ctx)          état initial, une fois le labyrinthe prêt et la distribution tirée
//   tick(state, ctx)     un tic de l'adversaire (cadence de core/level.js#getRatInterval)
//   enter(state, ctx)    le joueur vient d'entrer dans la case ctx.player
//   gain(state)          points en jeu
//   hints(state, ctx)    bulles d'aide après celle du joueur : [{ key, cell }] (clés i18n)
//   isIdle(state)        l'adversaire attend le premier déplacement du joueur (bulles d'aide)
// ctx = { grid, path, player, random } ; path est le plus court chemin, du départ du joueur
// (0, 0) au coin opposé.
//
// L'état porte aussi ce que le jeu et le rendu lisent, quel que soit le scénario :
//   ticks, budget         tics écoulés, tics avant la fin du temps
//   rival {x,y}, heading  adversaire et sens horizontal de sa marche (-1, 0, 1)
//   trail [{x,y}]         cases parcourues par l'adversaire, de la plus ancienne à la sienne
//   goal {x,y}|null, goalScale   l'action posée dans le labyrinthe, et sa part restante (0..1)
//   items [{x,y}]         objets à ramasser, dessinés avec l'emoji de l'action
//   rivalBadge, playerBadge      part (0..1) de l'action affichée en petit sur l'adversaire / le joueur
//   fog                   rayon visible autour du joueur (cases), ou null
//   ping                  adversaire visible à travers le brouillard pendant ce tic
//   sight [{x,y}]         cases vues par l'adversaire
//   phase                 'music' | 'warn' | 'freeze' (danse)
//   blink                 l'adversaire va disparaître : le rendu l'efface en fondu doux (ui/renderer.js)
//   sendPlayerTo {x,y}|null      le joueur doit être renvoyé sur cette case (lu puis effacé par le jeu)
//   result, reason        null tant que le niveau continue ; 'win' | 'survive' | 'lose' et sa cause
//                         (clé de la phrase de fin : scenario.<id>.<reason>, sinon slot.result.<result>)
import { DIRECTIONS } from '../maze.js';
import { POINTS_PER_CELL } from '../../config/constants.js';

export const TRAIL_MAX_CELLS = 30;
export const PREY_REST_EVERY = 3; // un adversaire qui fuit souffle un tic sur trois (duel : une fois le gant ramassé)

export const sameCell = (a, b) => !!a && !!b && a.x === b.x && a.y === b.y;

/** Cases voisines accessibles (sans mur entre les deux). */
export function neighbors(grid, cell) {
  const size = grid.length;
  const out = [];
  for (const dir of Object.values(DIRECTIONS)) {
    const x = cell.x + dir.dx;
    const y = cell.y + dir.dy;
    if (x < 0 || y < 0 || x >= size || y >= size) continue;
    if (grid[cell.y][cell.x].walls[dir.wall]) continue;
    out.push({ x, y });
  }
  return out;
}

/** Distance en pas de `from` à chaque case (tableau [y][x], -1 si inaccessible). */
export function distanceMap(grid, from) {
  const size = grid.length;
  const dist = Array.from({ length: size }, () => Array(size).fill(-1));
  dist[from.y][from.x] = 0;
  const queue = [from];
  for (let i = 0; i < queue.length; i++) {
    const cell = queue[i];
    for (const next of neighbors(grid, cell)) {
      if (dist[next.y][next.x] !== -1) continue;
      dist[next.y][next.x] = dist[cell.y][cell.x] + 1;
      queue.push(next);
    }
  }
  return dist;
}

/** Premier pas du plus court chemin de `from` vers `to` (from lui-même s'il y est déjà). */
export function stepToward(grid, from, to) {
  if (sameCell(from, to)) return { x: from.x, y: from.y };
  const dist = distanceMap(grid, to);
  let best = { x: from.x, y: from.y };
  for (const next of neighbors(grid, from)) {
    if (dist[next.y][next.x] !== -1 && dist[next.y][next.x] < dist[best.y][best.x]) best = next;
  }
  return best;
}

/**
 * Fuite : la case (sur place ou voisine) la plus loin du poursuivant ; à égalité, on préfère
 * bouger, au hasard.
 */
export function stepAway(grid, from, chaser, random = Math.random) {
  const dist = distanceMap(grid, chaser);
  const options = neighbors(grid, from);
  const far = Math.max(dist[from.y][from.x], ...options.map((c) => dist[c.y][c.x]));
  const best = options.filter((c) => dist[c.y][c.x] === far);
  if (best.length === 0) return { x: from.x, y: from.y };
  return best[Math.min(best.length - 1, Math.floor(random() * best.length))];
}

/** Cases vues en ligne droite depuis `from` dans la direction (dx, dy), jusqu'à un mur, au plus `max`. */
export function lineOfSight(grid, from, { dx, dy }, max) {
  const dir = Object.values(DIRECTIONS).find((d) => d.dx === dx && d.dy === dy);
  const seen = [];
  if (!dir) return seen;
  let cell = from;
  while (seen.length < max && !grid[cell.y][cell.x].walls[dir.wall]) {
    cell = { x: cell.x + dx, y: cell.y + dy };
    seen.push(cell);
  }
  return seen;
}

/** Ajoute une case au bout de la trace de l'adversaire (sans doublon, longueur bornée). */
export function extendRivalTrail(trail, cell) {
  if (sameCell(trail.at(-1), cell)) return trail;
  return [...trail, { x: cell.x, y: cell.y }].slice(-TRAIL_MAX_CELLS);
}

/** Sens horizontal d'un pas, pour retourner un sprite qui regarde à gauche. */
export const headingOf = (from, to) => Math.sign(to.x - from.x);

/**
 * Points d'un scénario au temps compté : tous pendant la première moitié du temps, puis en
 * proportion du temps restant, jusqu'à zéro.
 */
export function timeGain(fullGain, ticks, budget) {
  if (budget <= 0) return 0;
  return Math.max(0, Math.round(fullGain * Math.min(1, (2 * (budget - ticks)) / budget)));
}

/**
 * Points en jeu d'un labyrinthe : POINTS_PER_CELL par case du plus court chemin. C'est le gain de presque tous les scénarios ; le
 * scoreboard l'affiche dès le bandit manchot, avant que l'action soit tirée (le scénario donne ensuite le sien).
 */
export const pathGain = (path) => Math.max(1, path.length - 1) * POINTS_PER_CELL;

/** Part du temps qui reste, de 1 au départ à 0 quand il est écoulé : le trait sous le scoreboard (ui/timeBar.js). */
export function timeLeft({ ticks = 0, budget = 0 }) {
  if (budget <= 0) return 1;
  return Math.max(0, Math.min(1, 1 - ticks / budget));
}

/** État commun d'un scénario au temps compté. */
export function baseState({ path }, { rival, budget, idleTicks, fullGain = pathGain(path), ...rest }) {
  return {
    ticks: 0,
    budget,
    idleTicks,
    fullGain,
    rival: { x: rival.x, y: rival.y },
    heading: 0,
    trail: [{ x: rival.x, y: rival.y }],
    goal: null,
    goalScale: 1,
    items: [],
    rivalBadge: null,
    playerBadge: null,
    fog: null,
    ping: false,
    sight: [],
    phase: null,
    blink: false,
    sendPlayerTo: null,
    result: null,
    reason: null,
    ...rest,
  };
}

/** Un tic de plus ; fin du niveau si le temps est écoulé. */
export function countTick(state) {
  const ticks = state.ticks + 1;
  if (state.result || ticks < state.budget) return { ...state, ticks };
  return { ...state, ticks, result: 'lose', reason: 'timeout' };
}

/** Termine le niveau (sans écraser un résultat déjà acquis). */
export const finish = (state, result, reason = result) => (state.result ? state : { ...state, result, reason });

/** Déplace l'adversaire d'une case, en prolongeant sa trace. */
export const moveRival = (state, to) => ({
  ...state,
  heading: headingOf(state.rival, to) || state.heading,
  rival: { x: to.x, y: to.y },
  trail: extendRivalTrail(state.trail, to),
});

/** Case tirée au hasard dans une liste. */
export const pickCell = (cells, random) => cells[Math.min(cells.length - 1, Math.floor(random() * cells.length))];
